import { HttpClient } from '@angular/common/http';
import { computed, Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { JsonSchema } from 'src/app/models/json-schema.model';
import { ConnectResponse } from '../../models/connect-response.model';
import { UpdateSourceResponse } from '../../models/feed.model';

@Injectable({
    providedIn: 'root'
})
export class FeedSourceService {
    private _feedSourceUrl$ = new BehaviorSubject<string>('');
    feedSourceUrl$ = this._feedSourceUrl$.asObservable();
    private http = inject(HttpClient);
    jsonSchema = signal<JsonSchema | null>(null);
    previewSourceId = signal<string>('');
    rootArrayPath = signal<string>('');

    readonly jsonSubSchema = computed(() => {
        const schema = this.jsonSchema();
        const path = this.rootArrayPath();
        if (schema && path) {
            return this.findSchemaByJmesPath(schema, path);
        }
        return null;
    });

    getJsonSchema(rootArrayPath: string): Observable<JsonSchema> {
        return this._feedSourceUrl$.pipe(
            switchMap(url => {
                const body = {
                    source: url,
                    previewSourceId: this.previewSourceId(),
                    exampleDataCount: 5,
                    rootArrayPath: rootArrayPath
                };

                return this.http.post<JsonSchema>(
                    `${environment.origins.feedsFearsomeProcessor}/GenerateJSONSchema`,
                    body
                );
            }),
            tap(schema => this.setJsonSchema(schema)),
            catchError(error => {
                console.error('Failed to fetch schema', error);
                return throwError(() => error);
            })
        );
    }

    getFeedSchema(): JsonSchema {
        const subJsonSchema = this.jsonSubSchema();
        const jsonSchema = this.jsonSchema();

        if (subJsonSchema) {
            return subJsonSchema;
        } else if (jsonSchema) {
            return jsonSchema;
        }
        throw new Error('Feed schema not found');
    }

    setJsonSchema(schema: JsonSchema) {
        this.jsonSchema.set(schema);
    }

    setPreviewSourceId(id: string) {
        this.previewSourceId.set(id);
    }

    setFeedSourceUrl(url: string) {
        this._feedSourceUrl$.next(url);
    }

    setRootArrayPath(path: string) {
        this.rootArrayPath.set(path);
    }

    connect(url: string): Observable<ConnectResponse> {
        return this.http
            .post<ConnectResponse>(`${environment.origins.feedsFearsomeProcessor}/Connect`, {
                source: url,
                exampleDataCount: 5
            })
            .pipe(
                catchError(error => {
                    console.error('Failed to connect', error);
                    return throwError(() => error);
                })
            );
    }

    updateSource(previewSourceId: string): Observable<UpdateSourceResponse> {
        return this._feedSourceUrl$.pipe(
            switchMap(url => {
                const body = {
                    sourceUrl: url,
                    previewSourceId
                };

                const response = this.http.put<UpdateSourceResponse>(
                    `${environment.origins.feedsFearsomeProcessor}/UpdateSource`,
                    body
                );

                return response;
            }),
            catchError(error => {
                console.error('Failed to update preview source', error);
                return throwError(() => error);
            })
        );
    }

    findSchemaByJmesPath(schema: JsonSchema, targetPath: string): JsonSchema | null {
        // Check if current schema matches the target path
        if (schema.jmesPath === targetPath) {
            return schema;
        }

        // Search in oneOf schemas
        if (schema.oneOf) {
            for (const oneOfSchema of schema.oneOf) {
                const result = this.findSchemaByJmesPath(oneOfSchema, targetPath);
                if (result) return result;
            }
        }

        // Search in object properties
        if (schema.type === 'object' && schema.properties) {
            for (const key in schema.properties) {
                const result = this.findSchemaByJmesPath(schema.properties[key], targetPath);
                if (result) return result;
            }
        }

        // Search in array items
        if (schema.type === 'array' && schema.items) {
            const items = Array.isArray(schema.items) ? schema.items[0] : schema.items;
            if (items) {
                const result = this.findSchemaByJmesPath(items, targetPath);
                if (result) return result;
            }
        }

        return null;
    }
}
